// ─────────────────────────────────────────────────────────────────────────────
// useElementSize — measures an element's real pixel box and keeps it current.
//
// Every hand-rolled SVG chart here needs this: the viewBox is set to the
// measured px size so 1 user unit == 1 px on both axes. Without that the SVG is
// stretched to fit, which thickens strokes unevenly and turns the round hover
// dot into an ellipse.
//
// It returns a CALLBACK ref, not an object ref, on purpose. Charts render a
// placeholder until their data arrives, so the plot box mounts LATE — a
// one-shot mount effect would measure nothing and leave the viewBox at its
// default forever. A callback ref fires on the real mount, whenever that is.
//
//   const [plotRef, { width, height }] = useElementSize({ width: 480 });
//   <div ref={plotRef}><svg viewBox={`0 0 ${width} ${height}`}/></div>
// ─────────────────────────────────────────────────────────────────────────────
import { useCallback, useEffect, useRef, useState } from "react";

export function useElementSize(initial = { width: 0, height: 0 }) {
  const [size, setSize] = useState(initial);
  const observerRef = useRef(null);

  const ref = useCallback((node) => {
    // Detaching from the previous node (or unmounting) must drop the observer,
    // or it keeps firing setState after the element is gone.
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    if (!node) return;

    const measure = () => {
      const r = node.getBoundingClientRect();
      setSize({
        width:  Math.round(r.width)  || initial.width,
        height: Math.round(r.height) || initial.height,
      });
    };

    measure();
    if (typeof ResizeObserver !== "undefined") {
      observerRef.current = new ResizeObserver(measure);
      observerRef.current.observe(node);
    }
    // `initial` is only a fallback for a zero-sized box; re-running on a new
    // object identity would needlessly tear the observer down every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => observerRef.current?.disconnect(), []);

  return [ref, size];
}

export default useElementSize;
